'use client';

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { CanvasMode } from '@/components/workflows/reviews/ReviewCanvas';

export interface ReviewControls {
  mode: CanvasMode;
  setMode: (mode: CanvasMode) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  fitToView: () => void;
  zoomLevel: number;
  uploadMore: () => void;
  uploading: boolean;
}

const ReviewControlsContext = createContext<ReviewControls | null>(null);

export function ReviewControlsProvider({ children }: { children: ReactNode }) {
  const [controls, setControls] = useState<ReviewControls | null>(null);

  return (
    <ReviewControlsInternalContext.Provider value={{ controls, setControls }}>
      <ReviewControlsContext.Provider value={controls}>
        {children}
      </ReviewControlsContext.Provider>
    </ReviewControlsInternalContext.Provider>
  );
}

// Internal context for registration
const ReviewControlsInternalContext = createContext<{
  controls: ReviewControls | null;
  setControls: (controls: ReviewControls | null) => void;
}>({ controls: null, setControls: () => {} });

export function useReviewControls(): ReviewControls | null {
  return useContext(ReviewControlsContext);
}

export function useRegisterReviewControls() {
  const { setControls } = useContext(ReviewControlsInternalContext);

  const register = useCallback((controls: ReviewControls) => {
    setControls(controls);
  }, [setControls]);

  const unregister = useCallback(() => {
    setControls(null);
  }, [setControls]);

  return { register, unregister };
}
